import React, { useEffect } from 'react';
import {
  Alert,
  AlertDescription,
  Button,
  DateRangePicker,
  EmptyState,
  IconButton,
  SearchInput,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@jem-open/jem-ui';
import { ArrowDown, Loader2 } from 'lucide-react';
import { useHistory } from 'react-router';
import axios from 'axios';
import {
  ElasticMessage,
  ElasticMessageData,
} from '../../../types/MessageTypes';
import WhatsAppMessage from './WhatsAppMessage';
import { environment } from '../../../environment/environment';

type DateRange = { from: Date | undefined; to?: Date };

const DEFAULT_DAYS_BACK = 30;

const defaultDateRange = (): { from: Date; to: Date } => {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - DEFAULT_DAYS_BACK);
  return { from, to };
};

export default function MessagesList() {
  const history = useHistory();
  const [loadingMessages, setLoadingMessages] = React.useState(false);
  const [messages, setMessages] = React.useState<ElasticMessage[]>([]);
  const [dateRange, setDateRange] = React.useState<DateRange>(defaultDateRange);
  const [lookupKey, setLookupKey] = React.useState<string>('phone_number');
  const [lookupValue, setLookupValue] = React.useState<string>('');
  const [page, setPage] = React.useState<number>(1);
  const [hasMore, setHasMore] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string>('');

  const loadMessages = (pageNumber: number) => {
    setLoadingMessages(true);
    setError('');

    const from = dateRange.from ?? defaultDateRange().from;
    const to = dateRange.to ?? from;

    const beginningStartDate = new Date(from);
    beginningStartDate.setHours(0, 0, 0, 0);
    const startTimestamp = beginningStartDate.getTime() / 1000;

    const endofEndDate = new Date(to);
    endofEndDate.setHours(23, 59, 59, 0);
    const endTimestamp = endofEndDate.getTime() / 1000;

    const url = `${environment.baseUrl}/elastic_search/messages?lookup_key=${lookupKey}&lookup_value=${lookupValue}&start_date=${startTimestamp}&end_date=${endTimestamp}&page=${pageNumber}`;
    axios
      .get(url)
      .then((response) => response.data)
      .then((jsonResult: ElasticMessageData) => {
        setMessages((prev) =>
          pageNumber === 1 ? jsonResult.data : [...prev, ...jsonResult.data]
        );
        setHasMore(jsonResult.data.length > 0);
        setLoadingMessages(false);
        setPage(pageNumber);
      })
      .catch((err) => {
        setLoadingMessages(false);
        setError(err.message);
      });
  };

  useEffect(() => {
    setPage(1);
    if (messages.length > 0) {
      setMessages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupKey]);

  return (
    <div className="min-h-screen bg-white p-md">
      <div
        className="-mx-md -mt-md mb-md px-md py-lg"
        style={{ backgroundColor: 'rgba(237,244,251,0.5)' }}>
        <h1 className="m-none font-heading text-2xl font-semibold text-greyscale-text-title">
          Messages
        </h1>
        <p className="m-none mt-xxs max-w-2xl text-sm text-greyscale-text-body">
          Search and review conversations across messaging channels.
        </p>
      </div>

      <div className="mx-auto w-full max-w-6xl">
        <div className="mb-md flex flex-col gap-sm rounded-xl border border-greyscale-border-default bg-white p-sm shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-sm">
            <Tabs value="whatsapp">
              <TabsList variant="line">
                <TabsTrigger variant="line" value="whatsapp">
                  WhatsApp
                </TabsTrigger>
                <TabsTrigger variant="line" value="ussd" disabled>
                  USSD
                </TabsTrigger>
                <TabsTrigger variant="line" value="education" disabled>
                  Education
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="flex flex-wrap gap-xs">
              <Button
                variant="outline"
                size="sm"
                onClick={() => history.push('/admin/smslogs')}>
                SMS Logs
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => history.push('/admin/questions/education')}>
                Education questions
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-sm">
            <Tabs value={lookupKey} onValueChange={setLookupKey}>
              <TabsList>
                <TabsTrigger value="phone_number">Phone Number</TabsTrigger>
                <TabsTrigger value="id">ID</TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="min-w-[200px] flex-1">
              <SearchInput
                inputMode="numeric"
                placeholder={
                  lookupKey === 'phone_number'
                    ? 'Search by phone number'
                    : 'Search by employee ID'
                }
                value={lookupValue}
                onChange={(e) => setLookupValue(e.target.value)}
                onClear={() => setLookupValue('')}
              />
            </div>

            <DateRangePicker
              dateRange={dateRange}
              onDateRangeChange={(range) =>
                setDateRange((range as DateRange | undefined) ?? defaultDateRange())
              }
              placeholder="Pick a date range"
            />

            <Button
              variant="primary"
              loading={loadingMessages}
              disabled={loadingMessages || lookupValue.length === 0}
              onClick={() => loadMessages(1)}>
              Search
            </Button>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-md">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {messages.length === 0 && !loadingMessages && (
          <EmptyState
            icon="inbox"
            title="No messages found"
            description="Adjust your search or date range, then search again."
            variant="card"
          />
        )}

        <div className="flex flex-col gap-xs">
          {messages.map((message) => (
            <WhatsAppMessage message={message} key={message.timestamp} />
          ))}

          {!loadingMessages && messages.length > 0 && hasMore && (
            <div className="flex justify-center p-sm">
              <Button variant="outline" onClick={() => loadMessages(page + 1)}>
                Load older messages
              </Button>
            </div>
          )}

          {loadingMessages && (
            <div className="flex justify-center py-md">
              <Loader2 className="h-6 w-6 animate-spin text-secondary-text-label" />
            </div>
          )}
        </div>
      </div>

      <IconButton
        shape="circle"
        aria-label="Scroll to latest messages"
        className="fixed bottom-[20px] right-[60px] shadow-md"
        icon={<ArrowDown className="h-4 w-4" />}
        onClick={() =>
          window.scrollTo({
            top: document.body.scrollHeight,
            behavior: 'smooth',
          })
        }
      />
    </div>
  );
}
